/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as CertificateIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material'
import Subheader from 'src/v3/components/subheader'
import useSession from 'src/authentication/hooks/use-session'
import CertificateBuilder from 'src/v3/components/certificate-builder/certificate-builder'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { EditIcon } from 'src/v3/core/icons'
import {
  fetchLinkedServices,
  selectLinkedServicesByIds,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
  selectActiveLinkedService,
} from '../manage.slice'
import BasicInfo from './basic-info'
import AssignmentCompletionThreshold from './assignment-completion-threshold'
import ServiceNameEditor from './service-name-editor'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let session = useSession()
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedServices = useAppSelector(
    selectLinkedServicesByIds(session?.linkedServices ?? []),
  )
  let selectedLinkedService = linkedServices.find(
    (service) => service.id === activeLinkedService,
  )
  let [showCertificateBuilder, setShowCertificateBuilder] = useState(false)

  React.useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus, dispatch])

  let handleLinkedServiceChange = (value: string) => {
    dispatch(activeLinkedServiceChanged(value))
  }

  let handleCertificateComplete = (blob: Blob) => {
    // Handle the completed certificate blob
    // You can save it to server, download, etc.
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'certificate.png'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setShowCertificateBuilder(false)
  }

  return (
    <>
      <Subheader title="Linked Service Management" />
      {/* Service Selector */}
      <Paper elevation={1} sx={{ p: 3, mb: 6 }}>
        <FormControl fullWidth>
          <InputLabel id="linked-service-select-label">
            Select Linked Service
          </InputLabel>
          <Select
            labelId="linked-service-select-label"
            value={activeLinkedService || 'unselected'}
            label="Select Linked Service"
            onChange={(e) => handleLinkedServiceChange(e.target.value)}
            sx={{ minHeight: 56 }}
          >
            <MenuItem value="unselected" disabled>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 32, height: 32 }}>LS</Avatar>
                <Typography>No service selected</Typography>
              </Box>
            </MenuItem>
            {linkedServices.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${service.imageName}`}
                    sx={{ width: 32, height: 32 }}
                  >
                    {service.name?.charAt(0) || 'LS'}
                  </Avatar>
                  <Typography>{service.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      {selectedLinkedService && (
        <>
          <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
            <BasicInfo />
            <Card elevation={2}>
              <CardContent sx={{ minHeight: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SettingsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Basic Settings
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Configure basic information and service details.
                </Typography>

                {/* Service Name Editing */}
                <ServiceNameEditor />
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent sx={{ minHeight: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Assignment Thresholds
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Completion Threshold
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          Sets a window (in months) during which a user&apos;s
                          prior course completion counts toward an assignment.
                          When a manager creates a new assignment and a user has
                          already completed the course within this window, the
                          manager is prompted to decide whether to grant those
                          users automatic credit — skipping the assignment
                          entirely.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Scheduled Assignment Thresholds
                        </Typography>
                        <Typography variant="body2">
                          When enabled, this setting automatically applies the
                          completion threshold to assignments created by a
                          scheduled assignment. Because scheduled assignments
                          are created by an automated process, any user whose
                          prior completion falls within the threshold window
                          will receive credit automatically — without a manager
                          reviewing or selecting them. This is different from
                          manual assignment creation, where the manager is given
                          the choice to apply credit selectively.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                    slotProps={{
                      tooltip: { sx: { maxWidth: 360 } },
                    }}
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{
                        ml: 1,
                        color: 'text.secondary',
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Set completion thresholds and requirements for assignments.
                </Typography>

                {/* Assignment Completion Threshold */}
                <AssignmentCompletionThreshold />
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CertificateIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Certificate Designer
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Create custom certificates with your branding.
                </Typography>

                {selectedLinkedService?.certificateImageName && (
                  <Box
                    sx={{
                      mt: 2,
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Current Certificate Design:
                    </Typography>
                    <img
                      src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${selectedLinkedService.certificateImageName.replace('originals', 'resized')}`}
                      alt="Certificate Design"
                      style={{
                        maxWidth: '100%',
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                )}
                {!selectedLinkedService && (
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    No service selected
                  </Typography>
                )}
                <CardActions sx={{ p: 0 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setShowCertificateBuilder(true)}
                    sx={{ textTransform: 'none' }}
                    disabled={!selectedLinkedService}
                  >
                    {selectedLinkedService?.certificateImageName
                      ? 'Edit'
                      : 'Design'}{' '}
                    Certificate
                  </Button>
                </CardActions>
              </CardContent>
            </Card>
          </div>

          {/* Service Logo Section */}

          {/* Certificate Builder Modal */}
          {showCertificateBuilder && (
            <CertificateBuilder
              onComplete={handleCertificateComplete}
              onCancel={() => setShowCertificateBuilder(false)}
              linkedServiceId={activeLinkedService}
            />
          )}
        </>
      )}
    </>
  )
}
